import React, { useEffect, useState } from "react";
import { get_payment_settings, save_payment_settings } from "../../../api/Dashboard";

// Online payment for this campus.
//
// Every campus holds its own gateway account, so the keys live on the branch
// rather than in one shared setting. Nothing here is required: a campus with
// payment switched off still receives enrolment requests and rings the family,
// which is how all of them work today. Switching it on only adds the option to
// pay by card on the spot.
//
// The secret key is write-only. Once saved it is never sent back to this
// screen — it can be replaced, not read.

const BRAND = "#A6192E";

const s = {
  page: { padding: "0 0 40px" },
  intro: { fontSize: 13, color: "#6b7280", margin: "0 0 18px", lineHeight: 1.7, maxWidth: 780 },
  panel: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "18px 18px 20px", marginBottom: 18, maxWidth: 780,
  },
  panelTitle: { fontSize: 14, fontWeight: 700, color: "#1f2937", margin: "0 0 4px" },
  panelNote: { fontSize: 12.5, color: "#6b7280", margin: "0 0 16px", lineHeight: 1.6 },
  statusRow: {
    display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
    borderRadius: 8, marginBottom: 18,
  },
  on: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534" },
  off: { background: "#f8fafc", border: "1px solid #e2e8f0", color: "#475569" },
  statusText: { fontSize: 13, fontWeight: 600, margin: 0 },
  statusSub: { fontSize: 12, margin: "3px 0 0", opacity: 0.85, lineHeight: 1.5 },
  field: { marginBottom: 16 },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 },
  hint: { fontSize: 11.5, color: "#9ca3af", margin: "5px 0 0", lineHeight: 1.5 },
  input: {
    width: "100%", padding: "10px 13px", fontSize: 13, color: "#1f2937",
    border: "1px solid #e5e7eb", borderRadius: 8, outline: "none",
    background: "#fff", boxSizing: "border-box", fontFamily: "monospace",
  },
  check: { display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 18 },
  checkTitle: { fontSize: 13.5, fontWeight: 600, color: "#1f2937", margin: 0 },
  checkBody: { fontSize: 12, color: "#6b7280", margin: "3px 0 0", lineHeight: 1.55 },
  save: {
    background: BRAND, color: "#fff", border: "none", borderRadius: 8,
    padding: "10px 24px", fontSize: 13.5, fontWeight: 600, cursor: "pointer",
  },
  err: {
    fontSize: 12.5, color: BRAND, background: "#fef2f2", border: "1px solid #fee2e2",
    borderRadius: 8, padding: "10px 14px", marginBottom: 14,
  },
};

const PaymentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [publishable, setPublishable] = useState("");
  const [secret, setSecret] = useState("");
  const [secretSet, setSecretSet] = useState(false);
  const [branchName, setBranchName] = useState("");

  const load = async () => {
    const res = await get_payment_settings();
    const d = res?.data;
    if (d) {
      setEnabled(Boolean(d.payment_enabled));
      setPublishable(d.publishable_key || "");
      setSecretSet(Boolean(d.secret_key_set));
      setBranchName(d.branch_name || "");
    } else {
      setError(res?.message || "Could not load your payment settings.");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const res = await save_payment_settings({
      payment_enabled: enabled ? 1 : 0,
      publishable_key: publishable.trim(),
      // Blank means "leave the stored one alone", so it is only sent when typed.
      ...(secret.trim() ? { secret_key: secret.trim() } : {}),
    });
    setSaving(false);
    if (!res?.status) return setError(res?.message || "Could not save.");
    setSecret("");
    load();
  };

  if (loading) {
    return <div style={{ padding: "24px 0", fontSize: 13, color: "#9ca3af" }}>Loading…</div>;
  }

  return (
    <div style={s.page}>
      <p style={s.intro}>
        Card payment for {branchName || "this campus"}. Leave it off and nothing
        changes &mdash; enrolment requests still come through and you call the
        family to take payment. Turn it on and families can pay by card when
        they enrol, into your own account.
      </p>

      <div style={{ ...s.statusRow, ...(enabled ? s.on : s.off) }}>
        <div>
          <p style={s.statusText}>
            {enabled ? "Card payment is on" : "Card payment is off"}
          </p>
          <p style={s.statusSub}>
            {enabled
              ? "Families see a Pay now button on their enrolment summary. A 1% card surcharge is added."
              : "Families are told you will contact them about payment."}
          </p>
        </div>
      </div>

      <form onSubmit={submit}>
        <div style={s.panel}>
          <p style={s.panelTitle}>Your Stripe account</p>
          <p style={s.panelNote}>
            From your Stripe dashboard, under Developers &rsaquo; API keys. Use
            your test keys first &mdash; nothing is charged and you can run a
            whole enrolment through with a test card.
          </p>

          {error && <div style={s.err}>{error}</div>}

          <div style={s.field}>
            <label style={s.label}>Publishable key</label>
            <input
              style={s.input}
              value={publishable}
              onChange={(e) => setPublishable(e.target.value)}
              placeholder="pk_test_… or pk_live_…"
            />
            <p style={s.hint}>
              Starts with <code>pk_</code>. This one is safe to be seen &mdash;
              it is sent to the family&rsquo;s browser.
            </p>
          </div>

          <div style={s.field}>
            <label style={s.label}>
              Secret key {secretSet && <span style={{ color: "#16a34a" }}>&mdash; saved</span>}
            </label>
            <input
              style={s.input}
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={secretSet ? "Leave blank to keep the saved key" : "sk_test_… or sk_live_…"}
              autoComplete="new-password"
            />
            <p style={s.hint}>
              Starts with <code>sk_</code>. Never shown again once saved
              &mdash; type a new one to replace it.
            </p>
          </div>
        </div>

        <div style={s.panel}>
          <label style={s.check}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              style={{ marginTop: 3, width: 15, height: 15, accentColor: BRAND }}
            />
            <span>
              <p style={s.checkTitle}>Take card payments online</p>
              <p style={s.checkBody}>
                Needs both keys above. Turn this off at any time &mdash; it stops
                the Pay now button without touching anything already paid.
              </p>
            </span>
          </label>

          <button type="submit" style={s.save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentSettings;
